// frontend/src/utils/challan.js
//
// Cumulative statement math for one student.
// Pure functions — no React, no API.

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function monthName(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

export function monthKeyOf(dateStr) {
  if (!dateStr) return null;
  return String(dateStr).substring(0, 7); // YYYY-MM
}

export function balanceOf(fee) {
  return Math.max(0, (fee.amount || 0) - (fee.paid_amount || 0));
}

export function isFullyPaid(fee) {
  if (!fee) return false;
  return balanceOf(fee) <= 0.01;
}

function isOverdue(fee, today) {
  if (!fee.due_date) return false;
  return new Date(fee.due_date) < today;
}

function lateFeeFor(student, feeType, structures) {
  if (!student || !structures || structures.length === 0) return 0;
  const s = structures.find(
    x => x.class_id === student.class_id && x.fee_type === feeType
  );
  return s?.late_fee || 0;
}

// Build a fee item with all fields the UI needs to render
// gross → discount → net breakdown.
function buildFeeItem(fee, opts = {}) {
  const base = fee.base_amount ?? fee.amount ?? 0;
  const net = fee.amount || 0;
  const discountPct = fee.discount_percentage || 0;
  const bal = balanceOf(fee);
  return {
    id: fee.id,
    kind: fee.fee_type === 'Late Fee' ? 'late' : 'tuition',
    type: fee.fee_type,
    label: fee.fee_type,
    base,
    discountPct,
    net,
    paid: fee.paid_amount || 0,
    balance: bal,
    dueDate: fee.due_date,
    isWaived: net <= 0.01 && base > 0.01,
    fullyPaid: opts.fullyPaid || bal <= 0.01,
    legacy: opts.legacy || false,
    computed: opts.computed || false,
  };
}

/**
 * Build the cumulative fee statement for a student.
 *
 * Rules:
 *   - Includes every fee for the student with due_date <= clicked month.
 *   - 100%-discounted (net = 0) fees are shown as waived, not dropped.
 *   - Late fee per month is honored from a stored row if it exists,
 *     otherwise computed from the class fee structure (only if the
 *     tuition in that month is unpaid AND overdue).
 *   - If the clicked fee is fully paid → receipt mode (single fee).
 */
export function buildStatement(allFees, clickedFee, structures = [], student = null) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const empty = {
    isPaid: false, months: [], grandTotal: 0,
    itemsToMarkPaid: [], lateFeesToCreate: [],
  };
  if (!clickedFee) return empty;

  // ── RECEIPT MODE ──────────────────────────────────────────
  // Only enter receipt mode for fees that were ACTUALLY PAID with
  // real money. A 100%-discounted fee has amount = 0 — that's a
  // waiver, not a payment — so we fall through to statement mode
  // and show the full cumulative history including previous dues.
  const clickedNet  = clickedFee.amount || 0;
  const clickedBase = clickedFee.base_amount ?? clickedNet;
  const clickedIsWaived = clickedNet <= 0.01 && clickedBase > 0.01;

  if (!clickedIsWaived && isFullyPaid(clickedFee)) {
    const item = buildFeeItem(clickedFee, { fullyPaid: true });
    return {
      isPaid: true,
      months: [{
        monthKey: monthKeyOf(clickedFee.due_date),
        monthLabel: monthName(clickedFee.due_date),
        isCurrent: true,
        subtotal: 0,
        items: [item],
      }],
      grandTotal: clickedFee.amount || 0,
      itemsToMarkPaid: [],
      lateFeesToCreate: [],
    };
  }

  // ── STATEMENT MODE ────────────────────────────────────────
  const clickedMonthKey = monthKeyOf(clickedFee.due_date);

  const relevant = (allFees || []).filter(f =>
    f.student_id === clickedFee.student_id &&
    f.due_date &&
    monthKeyOf(f.due_date) <= clickedMonthKey
  );

  const buckets = new Map();
  relevant.forEach(f => {
    const k = monthKeyOf(f.due_date);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(f);
  });
  if (!buckets.has(clickedMonthKey)) buckets.set(clickedMonthKey, [clickedFee]);

  const sortedKeys = [...buckets.keys()].sort().reverse(); // newest first

  const months = [];
  const itemsToMarkPaid = [];
  const lateFeesToCreate = [];

  for (const k of sortedKeys) {
    const bucket = buckets.get(k);
    const isCurrent = k === clickedMonthKey;

    const items = [];
    let subtotal = 0;

    const nonLate = bucket.filter(f => f.fee_type !== 'Late Fee');
    const storedLate = bucket.find(f => f.fee_type === 'Late Fee');

    // Regular fees for this month
    for (const f of nonLate) {
      const bal = balanceOf(f);
      const base = f.base_amount ?? f.amount ?? 0;
      const net = f.amount || 0;
      const isWaived = net <= 0.01 && base > 0.01;

      // Show if there is a balance owed OR the fee was waived.
      // Fully-paid non-waived fees are hidden (they belong on a receipt).
      if (bal <= 0.01 && !isWaived) continue;

      items.push(buildFeeItem(f));

      if (bal > 0.01) {
        subtotal += bal;
        itemsToMarkPaid.push({ id: f.id, payAmount: net });
      }
    }

    // Late fee — stored wins, else computed
    if (storedLate) {
      const bal = balanceOf(storedLate);
      if (bal > 0.01) {
        items.push(buildFeeItem(storedLate, { legacy: true }));
        subtotal += bal;
        itemsToMarkPaid.push({ id: storedLate.id, payAmount: storedLate.amount || 0 });
      }
    } else {
      const unpaidTuition = nonLate.find(f =>
        f.fee_type === 'Tuition' && balanceOf(f) > 0.01
      );
      if (unpaidTuition && isOverdue(unpaidTuition, today)) {
        const amt = lateFeeFor(student, 'Tuition', structures);
        if (amt > 0) {
          items.push({
            id: null,
            kind: 'late',
            type: 'Late Fee',
            label: 'Late Fee',
            base: amt,
            discountPct: 0,
            net: amt,
            paid: 0,
            balance: amt,
            dueDate: unpaidTuition.due_date,
            isWaived: false,
            fullyPaid: false,
            computed: true,
          });
          subtotal += amt;
          lateFeesToCreate.push({
            student_id: clickedFee.student_id,
            fee_type: 'Late Fee',
            amount: amt,
            base_amount: amt,
            due_date: unpaidTuition.due_date,
            paid_amount: amt,
            status: 'Paid',
            notes: `Late fee for ${monthName(unpaidTuition.due_date)}`,
          });
        }
      }
    }

    // Include month if it has items, OR it's the clicked month.
    if (items.length > 0 || isCurrent) {
      months.push({
        monthKey: k,
        monthLabel: monthName(k + '-01'),
        isCurrent,
        items,
        subtotal,
      });
    }
  }

  const grandTotal = months.reduce((s, m) => s + m.subtotal, 0);

  return { isPaid: false, months, grandTotal, itemsToMarkPaid, lateFeesToCreate };
}

// ─────────────────────────────────────────────────────────────
// Table-only helpers (flat list of previous unpaid fees)
// ─────────────────────────────────────────────────────────────

export function getPreviousDuesFromList(allFees, currentFee) {
  if (!currentFee) return [];
  const curDate = new Date(currentFee.due_date);
  return (allFees || [])
    .filter(f =>
      f.student_id === currentFee.student_id &&
      f.id !== currentFee.id &&
      f.status !== 'Paid' &&
      new Date(f.due_date) < curDate &&
      ((f.amount || 0) - (f.paid_amount || 0)) > 0.01
    )
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
}

export function computePreviousBalance(previousDues) {
  return (previousDues || []).reduce(
    (s, f) => s + Math.max(0, (f.amount || 0) - (f.paid_amount || 0)),
    0
  );
}